<template>
  <!-- type=1 HTML container：有 children 走遞迴 -->
  <div v-if="renderType === RenderTypeEnum.HTML && hasChildren">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" />
  </div>

  <!-- type=1 HTML leaf：inline render，讓相鄰文字 <br/> 正常 inline flow -->
  <div v-else-if="renderType === RenderTypeEnum.HTML" class="inline" v-html="meta"></div>

  <!-- type=2 img / type=3 youtube：leaf media，結構一致只差 tag/attrs，用 config 驅動 -->
  <component :is="mediaTag" v-else-if="renderType === RenderTypeEnum.MEDIA" ref="mediaRef" v-bind="mediaAttrs" />

  <!-- type=4 ul / type=5 ol：走 list_v2 遞迴 li（保留巢狀層級）。舊 flat attr.list 已 deprecated，前端不再讀 -->
  <component :is="listTag" v-else-if="renderType === RenderTypeEnum.LIST">
    <content-data v-for="(listItem, idx) in list" :key="idx" :node="listItem" />
  </component>

  <!-- type=6 li：內含 1/2/3/4/5，走遞迴 -->
  <li v-else-if="renderType === RenderTypeEnum.LIST_ITEM">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" />
  </li>
</template>

<script setup lang="ts">
// ref: funnow.web.nuxt /components/ContentData.vue
import { ProductDetailHtmlTypeEnum, type ProductContentData } from '@/types/product'

interface Props {
  /** 「方案介紹」/「購買須知」靜態內容的單一 tree node */
  node: ProductContentData
}

enum RenderTypeEnum {
  HTML,
  MEDIA,
  LIST,
  LIST_ITEM,
}

defineOptions({ name: 'ContentData' })

const props = defineProps<Props>()

const mediaRef = useTemplateRef<HTMLElement>('mediaRef')

// 影片捲出視窗時暫停播放（圖片放大由父層 useLightbox 的 PhotoSwipe 直接掛在 <img> 上，不需 emit）
const { stop: stopObserveMedias } = useIntersectionObserver(
  mediaRef,
  entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting && entry.target instanceof HTMLIFrameElement) {
        entry.target.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), 'https://www.youtube.com')
      }
    })
  },
  {
    rootMargin: '0px',
    threshold: [0],
  }
)

const type = computed(() => props.node.type)
const meta = computed(() => props.node.meta ?? '')
const children = computed(() => props.node.children ?? [])
const hasChildren = computed(() => children.value.length > 0)
const list = computed(() => props.node.attr?.list_v2 ?? [])
const listTag = computed(() => (type.value === ProductDetailHtmlTypeEnum.ORDERED_LIST ? 'ol' : 'ul'))

const mediaTag = computed(() => {
  switch (type.value) {
    case ProductDetailHtmlTypeEnum.IMAGE:
      return 'img'
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      return 'iframe'
    default:
      return 'div'
  }
})

const renderType = computed(() => {
  switch (type.value) {
    case ProductDetailHtmlTypeEnum.IMAGE:
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      return RenderTypeEnum.MEDIA
    case ProductDetailHtmlTypeEnum.UNORDERED_LIST:
    case ProductDetailHtmlTypeEnum.ORDERED_LIST:
      return RenderTypeEnum.LIST
    case ProductDetailHtmlTypeEnum.LIST_ITEM:
      return RenderTypeEnum.LIST_ITEM
    case ProductDetailHtmlTypeEnum.HTML:
    default:
      return RenderTypeEnum.HTML
  }
})

const mediaAttrs = computed(() => {
  const attrs: {
    src: string
    class: string
    style?: Record<string, unknown>
    allow?: string
    loading?: string
    referrerpolicy?: string
  } = {
    src: '',
    class: '',
  }

  switch (type.value) {
    case ProductDetailHtmlTypeEnum.IMAGE: {
      const width = props.node.attr?.width ?? 0
      const height = props.node.attr?.height ?? 0

      attrs.src = meta.value
      attrs.class = 'w-full cursor-pointer'
      attrs.style = { 'aspect-ratio': width && height ? `${width}/${height}` : undefined }
      break
    }
    case ProductDetailHtmlTypeEnum.YOUTUBE: {
      attrs.src = `https://www.youtube.com/embed/${meta.value}?enablejsapi=1`
      attrs.class = 'w-full aspect-video'
      attrs.allow = 'fullscreen'
      attrs.loading = 'lazy'
      attrs.referrerpolicy = 'strict-origin-when-cross-origin'
      break
    }
  }

  return attrs
})

onUnmounted(() => {
  stopObserveMedias()
})
</script>

<style lang="scss" scoped></style>
